import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useZonas } from "../../calendar/hooks/useZonas";
import { Reservation } from "../../reservations/types/Reservation";
import { FormData, FormErrors } from "../types";
import { useReservaValidacion } from "../../reservations/hooks/useReservaValidacion";
import {
  obtenerCategoriaDepartamento,
  DepartamentoCategoria,
} from "../../departments/types/Departamento";
import { SelectChangeEvent } from "@mui/material";
import { RangeKeyDict } from "react-date-range";
import { CalculationService } from "../../../services/calculationService";
import { Cliente, useClienteSearch } from "./useClienteSearch";
import { useCreateReservation } from "../../../hooks/useReservationMutations";
import { useCreateEstacionamientoReservation } from "../../../hooks/useEstacionamientoMutations";
import { ReservaEstacionamiento } from "../../../types/ReservaEstacionamiento";
import { logger } from "../../../utils/logger";
import {
  validateForm,
  validateEstacionamientoForm,
  validateDocumentoDirecto,
} from "../helpers";
import {
  etiquetaClienteMostrar,
  onlyDigits,
} from "../../clients/utils/clientIdentityHelpers";
import { isInternalDepartment } from "../../calendar/utils/internalDepartments";
import { useCambioTipoCliente } from "../../clients/hooks/useCambioTipoCliente";
import { formatDateKey, parseDateOnly } from "../../shared/utils/dateUtils";

interface UseNewReservationFormProps {
  onClose: () => void;
  onSuccess: (reservation: Reservation | ReservaEstacionamiento) => void;
  existingReservations?: Reservation[];
  initialData?: Partial<Reservation> & { estacionamiento?: string };
  soloEstacionamiento?: boolean;
}

const getErrorStatus = (error: unknown): number | undefined =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  typeof (error as { response?: { status?: unknown } }).response?.status ===
    "number"
    ? (error as { response?: { status?: number } }).response?.status
    : undefined;

export const useNewReservationForm = ({
  onSuccess,
  existingReservations = [],
  initialData,
  soloEstacionamiento = false,
}: UseNewReservationFormProps) => {
  const { overlapError, checkOverlap } = useReservaValidacion();
  const { zonas, loading: loadingZonas, error: errorZonas } = useZonas();
  const { validarDNIExistente } = useClienteSearch();
  const createReservationMutation = useCreateReservation();
  const createEstacionamientoReservationMutation =
    useCreateEstacionamientoReservation();
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [esEmpresa, setEsEmpresa] = useState(false);
  const [esBooking, setEsBooking] = useState(false);
  const [esAirbnb, setEsAirbnb] = useState(false);
  const [documentoWarning, setDocumentoWarning] = useState<string | undefined>();

  const [formData, setFormData] = useState<FormData>(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const initialFormData: FormData = {
      entrada: initialData ? new Date(initialData.entrada) : today,
      salida: initialData ? new Date(initialData.salida) : tomorrow,
      departamento: "",
      estacionamiento: initialData?.estacionamiento || "",
      cantidad_huespedes: Number(initialData?.cantidad_huespedes) || 1,
      valorTotal: Number(initialData?.valor_total) || 0,
      sena: Number(initialData?.pago) || 0,
      nombre: initialData?.nombre || "",
      apellido: initialData?.apellido || "",
      dni: initialData?.dni || "",
      edad: "",
      localidad: "",
      notas: initialData?.notas || "",
      email1: initialData?.email1 || "",
      email2: initialData?.email2 || "",
      telefono1: initialData?.telefono1 || "",
      telefono2: initialData?.telefono2 || "",
      acompanantes: [],
      tipo_cama: initialData?.tipo_cama
        ? Array.isArray(initialData.tipo_cama)
          ? initialData.tipo_cama
          : [initialData.tipo_cama]
        : ["individual"],
      afiliado_nombre: initialData?.afiliado_nombre || "",
      afiliado_apellido: initialData?.afiliado_apellido || "",
      afiliado_dni: initialData?.afiliado_dni || "",
      late_checkout: initialData?.late_checkout || false,
      sin_limpieza_automatica: initialData?.sin_limpieza_automatica || false,
      // 🎯 Campos faltantes para ajustes financieros
      ajuste_financiero: Number(initialData?.ajuste_financiero) || 0,
      tipo_ajuste: initialData?.tipo_ajuste || "ninguno",
      // 🎯 Campos faltantes para horarios
      hora_inicio: initialData?.hora_inicio || "14:00",
      hora_fin: initialData?.hora_fin || "10:00",
    };
    return initialFormData;
  });
  const isInternalStay = isInternalDepartment(formData.departamento);

  const docValidationSeqRef = useRef(0);
  const formDataRef = useRef(formData);
  const esEmpresaRef = useRef(esEmpresa);
  const esBookingRef = useRef(esBooking);
  const esAirbnbRef = useRef(esAirbnb);
  const selectedClienteRef = useRef<Cliente | null>(selectedCliente);

  useLayoutEffect(() => {
    formDataRef.current = formData;
    esEmpresaRef.current = esEmpresa;
    esBookingRef.current = esBooking;
    esAirbnbRef.current = esAirbnb;
    selectedClienteRef.current = selectedCliente;
  });

  const runDirectoDocumentoValidation = useCallback(
    (rawDni: string, modeEmpresa: boolean) => {
      const cid =
        formDataRef.current.cliente_id ?? selectedClienteRef.current?.id;
      if (cid != null && Number(cid) > 0) {
        docValidationSeqRef.current += 1;
        setDocumentoWarning(undefined);
        setErrors((prev) => ({ ...prev, dni: undefined }));
        return;
      }

      const seq = ++docValidationSeqRef.current;
      const docRes = validateDocumentoDirecto(rawDni, modeEmpresa);

      if (!docRes.shouldLookupCliente) {
        if (seq !== docValidationSeqRef.current) return;
        setDocumentoWarning(undefined);
        setErrors((prev) => ({
          ...prev,
          dni: docRes.syncError || undefined,
        }));
        return;
      }

      if (docRes.syncError) {
        setDocumentoWarning(undefined);
        setErrors((prev) => ({ ...prev, dni: docRes.syncError }));
      }

      validarDNIExistente(rawDni).then((clienteExistente) => {
        if (seq !== docValidationSeqRef.current) return;
        if (onlyDigits(formDataRef.current.dni || "") !== docRes.digits) return;
        if (esEmpresaRef.current !== modeEmpresa) return;

        const sel = selectedClienteRef.current;
        if (
          sel &&
          clienteExistente &&
          clienteExistente.id === sel.id
        ) {
          setDocumentoWarning(undefined);
          setErrors((prev) => ({ ...prev, dni: undefined }));
          return;
        }

        if (modeEmpresa) {
          if (!clienteExistente) {
            setDocumentoWarning(undefined);
            setErrors((prev) => ({ ...prev, dni: undefined }));
            return;
          }
          const etiqueta = etiquetaClienteMostrar(clienteExistente);
          if (clienteExistente.es_empresa) {
            setErrors((prev) => ({ ...prev, dni: undefined }));
            setDocumentoWarning(
              `Este CUIT ya está cargado como ${etiqueta}. Si es la misma sucursal, seleccioná ese cliente; si es otra sucursal, podés continuar.`
            );
          } else {
            setDocumentoWarning(undefined);
            setErrors((prev) => ({
              ...prev,
              dni: `Este número coincide con una persona cargada como ${etiqueta}. Revisá si corresponde elegir «Persona».`,
            }));
          }
          return;
        }

        const len = docRes.digits.length;
        if (len === 11) {
          if (clienteExistente?.es_empresa) {
            const etiqueta = etiquetaClienteMostrar(clienteExistente);
            setDocumentoWarning(undefined);
            setErrors((prev) => ({
              ...prev,
              dni: `Este número coincide con una empresa cargada como ${etiqueta}. Elegí «Empresa» o seleccioná ese cliente.`,
            }));
          } else {
            setDocumentoWarning(undefined);
            setErrors((prev) => ({
              ...prev,
              dni:
                docRes.syncError ||
                "Este dato parece un CUIT. Si es una empresa, elegí «Empresa».",
            }));
          }
          return;
        }

        if (len === 7 || len === 8) {
          if (!clienteExistente) {
            setDocumentoWarning(undefined);
            setErrors((prev) => ({ ...prev, dni: undefined }));
            return;
          }
          const etiqueta = etiquetaClienteMostrar(clienteExistente);
          if (clienteExistente.es_empresa) {
            setDocumentoWarning(undefined);
            setErrors((prev) => ({
              ...prev,
              dni: "Este número coincide con una empresa, revisá el dato.",
            }));
          } else {
            setDocumentoWarning(undefined);
            setErrors((prev) => ({
              ...prev,
              dni: `Este DNI ya pertenece a ${etiqueta}. Seleccioná ese cliente desde Buscar cliente.`,
            }));
          }
        }
      });
    },
    [validarDNIExistente]
  );

  // Lógica para auto-rellenar datos del cliente si se asocia un departamento
  useEffect(() => {
    const findAndSetGuestData = async () => {
      if (formData.departamento_asociado && existingReservations) {
        // Encontrar la reserva principal más relevante
        const reservaPrincipal = [...existingReservations]
          .filter(
            (r) =>
              r.departamento === formData.departamento_asociado &&
              formData.entrada &&
              formData.salida &&
              parseDateOnly(r.entrada) < formData.salida &&
              parseDateOnly(r.salida) > formData.entrada
          )
          .sort(
            (a, b) =>
              new Date(b.entrada || 0).getTime() -
                new Date(a.entrada || 0).getTime() ||
              new Date(b.fecha_creacion || 0).getTime() -
                new Date(a.fecha_creacion || 0).getTime()
          )[0];

        if (reservaPrincipal) {
          setFormData((prev) => ({
            ...prev,
            nombre: reservaPrincipal.nombre,
            apellido: reservaPrincipal.apellido,
            dni: reservaPrincipal.dni,
            cliente_id: reservaPrincipal.cliente?.id,
          }));
        }
      }
    };

    findAndSetGuestData();
  }, [
    formData.departamento_asociado,
    existingReservations,
    formData.entrada,
    formData.salida,
    soloEstacionamiento,
  ]);

  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [categoriaDepartamento, setCategoriaDepartamento] =
    useState<DepartamentoCategoria | null>(null);
  const [dateRangeAnchorEl, setDateRangeAnchorEl] =
    useState<null | HTMLElement>(null);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return {
      startDate: initialData ? new Date(initialData.entrada) : today,
      endDate: initialData ? new Date(initialData.salida) : tomorrow,
      key: "selection",
    };
  });

  // Establecer el departamento cuando las zonas estén cargadas
  useEffect(() => {
    if (!loadingZonas && zonas.length > 0 && initialData?.departamento) {
      // Verificar que el departamento existe en las zonas cargadas
      const departamentoExiste = zonas.some((zona) =>
        zona.departamentos.includes(initialData.departamento)
      );

      if (departamentoExiste) {
        setFormData((prev) => ({
          ...prev,
          departamento: initialData.departamento,
        }));
        logger.debug("Departamento establecido", {
          departamento: initialData.departamento,
        });
      } else {
        logger.warn("Departamento no encontrado en zonas", {
          departamento: initialData.departamento,
        });
      }
    }
  }, [loadingZonas, zonas, initialData?.departamento]);

  // Cargar categoría del departamento cuando cambia
  useEffect(() => {
    const cargarCategoria = async () => {
      if (formData.departamento) {
        const categoria = await obtenerCategoriaDepartamento(
          formData.departamento
        );
        setCategoriaDepartamento(categoria || null);
      }
    };
    cargarCategoria();
  }, [formData.departamento]);

  // Calcular valor total automáticamente cuando cambian las fechas, departamento o cantidad de huéspedes
  useEffect(() => {
    const actualizarValorTotal = async () => {
      // Validar que tengamos todos los datos necesarios antes de hacer la llamada
      if (
        formData.entrada &&
        formData.salida &&
        formData.departamento &&
        formData.departamento.trim() !== ""
      ) {
        if (isInternalDepartment(formData.departamento)) {
          setFormData((prev: FormData) => ({
            ...prev,
            valorTotal: 0,
            sena: 0,
            precioPersonalizado: undefined,
            late_checkout: false,
            ajuste_financiero: 0,
            tipo_ajuste: "ninguno",
          }));
          return;
        }

        // Validar que las fechas sean válidas
        if (formData.salida <= formData.entrada) {
          return; // No calcular si las fechas no son válidas
        }

        try {
          // Usar el backend para calcular el valor total
          const resultado = await CalculationService.calcularValorTotal({
            entrada: formatDateKey(formData.entrada),
            salida: formatDateKey(formData.salida),
            departamento: formData.departamento,
            late_checkout: formData.late_checkout || false,
            precio_personalizado: formData.precioPersonalizado || undefined,
          });

          setFormData((prev: FormData) => ({
            ...prev,
            valorTotal: resultado.valor_total,
          }));
        } catch (error: unknown) {
          // No mostrar errores 400 ya que son esperados durante la edición del formulario
          if (getErrorStatus(error) !== 400) {
            logger.errorWithContext("Error al calcular valor total", error);
          }
          // No hay fallback: el backend es la única fuente de verdad para cálculos
        }
      }
    };

    // Debounce para evitar llamadas múltiples
    const timeoutId = setTimeout(actualizarValorTotal, 300);
    return () => clearTimeout(timeoutId);
  }, [
    formData.entrada,
    formData.salida,
    formData.departamento,
    formData.late_checkout,
    formData.precioPersonalizado,
    categoriaDepartamento,
  ]);

  // Calcular valor total del estacionamiento automáticamente
  useEffect(() => {
    const calcularValorEstacionamiento = () => {
      if (
        !formData.entrada ||
        !formData.salida ||
        !formData.precio_estacionamiento_por_dia
      ) {
        setFormData((prev) => ({
          ...prev,
          valor_total_estacionamiento: 0,
        }));
        return;
      }

      const dias = Math.ceil(
        (formData.salida.getTime() - formData.entrada.getTime()) /
          (1000 * 60 * 60 * 24)
      );
      const valorTotal = dias * formData.precio_estacionamiento_por_dia;

      setFormData((prev) => ({
        ...prev,
        valor_total_estacionamiento: valorTotal,
      }));
    };

    calcularValorEstacionamiento();
  }, [
    formData.entrada,
    formData.salida,
    formData.precio_estacionamiento_por_dia,
  ]);

  const IDENTITY_FIELDS = new Set([
    "nombre",
    "apellido",
    "dni",
    "telefono1",
    "email1",
    "localidad",
  ]);

  /** Desasocia cliente seleccionado y limpia avisos/errores del documento (evita cliente_id viejo). */
  const detachClienteIdentity = () => {
    docValidationSeqRef.current += 1;
    setSelectedCliente(null);
    selectedClienteRef.current = null;
    setDocumentoWarning(undefined);
    setErrors((prev) => ({ ...prev, dni: undefined }));
  };

  const detachSelectedClienteIfIdentityChanged = (fieldName: string) =>
    selectedCliente !== null && IDENTITY_FIELDS.has(fieldName);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const detach = detachSelectedClienteIfIdentityChanged(name);
    if (detach) {
      detachClienteIdentity();
    }

    let error = "";

    if (name === "dni") {
      const esReservaExterna = esBooking || esAirbnb;
      if (esReservaExterna) {
        setDocumentoWarning(undefined);
        if (value.trim().length === 0) {
          error = esBooking
            ? "El código de reserva Booking es obligatorio"
            : "El código de reserva Airbnb es obligatorio";
        }
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          ...(detach ? { cliente_id: undefined } : {}),
        }));
        setErrors((prev) => ({ ...prev, [name]: error }));
      } else {
        const numericValue = value.replace(/\D/g, "");

        setFormData((prev) => ({
          ...prev,
          [name]: numericValue,
          ...(detach ? { cliente_id: undefined } : {}),
        }));

        runDirectoDocumentoValidation(numericValue, esEmpresa);
      }
    } else if (name === "nombre" || name === "apellido") {
      const normalizedValue =
        value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      setFormData((prev) => ({
        ...prev,
        [name]: normalizedValue,
        ...(detach ? { cliente_id: undefined } : {}),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(detach ? { cliente_id: undefined } : {}),
      }));
    }
  };

  const handleInputChange = (field: keyof FormData, value: unknown) => {
    const detach =
      selectedCliente !== null && IDENTITY_FIELDS.has(field as string);
    if (detach) {
      detachClienteIdentity();
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value as FormData[keyof FormData],
      ...(detach ? { cliente_id: undefined } : {}),
    }));

    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSelectChange = async (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "departamento" && formData.entrada && formData.salida) {
      const reservaTemporal: Reservation = {
        id: 0,
        nombre: formData.nombre || "",
        departamento: value,
        entrada: formatDateKey(formData.entrada),
        salida: formatDateKey(formData.salida),
        estado: "confirmada",
        pago: 0,
      };

      checkOverlap(reservaTemporal, existingReservations);
    }
  };

  const handleDateRangeClick = (event: React.MouseEvent<HTMLElement>) => {
    setDateRangeAnchorEl(event.currentTarget);
  };

  const handleDateRangeClose = () => {
    setDateRangeAnchorEl(null);
  };

  const handleDateRangeChange = async (ranges: RangeKeyDict) => {
    const selection = ranges.selection;
    if (!selection.startDate || !selection.endDate) return;

    setDateRange({
      startDate: selection.startDate,
      endDate: selection.endDate,
      key: selection.key || "selection",
    });
    const newFormData = {
      ...formData,
      entrada: selection.startDate,
      salida: selection.endDate,
    };
    setFormData(newFormData);

    if (
      formData.departamento
    ) {
      const reservaTemporal: Reservation = {
        id: 0,
        nombre: formData.nombre || "",
        departamento: formData.departamento,
        entrada: formatDateKey(selection.startDate),
        salida: formatDateKey(selection.endDate),
        estado: "confirmada",
        pago: 0,
      };

      checkOverlap(reservaTemporal, existingReservations);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const continueWithReservation = async () => {
    setErrors((prev) => ({ ...prev, blacklist_warning: undefined }));
    setIsSubmitting(true);
    try {
      await createReservation();
    } finally {
      setIsSubmitting(false);
    }
  };

  const createReservation = async () => {
    try {
      const esReservaExterna = esBooking || esAirbnb;
      const origenReserva = esBooking
        ? "booking"
        : esAirbnb
          ? "airbnb"
          : "directo";

      const codigoExternoTrim = (formData.dni || "").trim();
      const codigoReservaExterna =
        esReservaExterna && codigoExternoTrim ? codigoExternoTrim : null;
      const esEstadiaInterna = isInternalDepartment(formData.departamento);

      const reservaData = {
        entrada: formatDateKey(formData.entrada!),
        salida: formatDateKey(formData.salida!),
        departamento: formData.departamento!,
        nombre: formData.nombre!.trim(),
        apellido: formData.apellido!.trim(),
        dni: esReservaExterna ? undefined : formData.dni!.trim(),
        localidad: formData.localidad?.trim() || "",
        email1: formData.email1?.trim() || "",
        email2: formData.email2?.trim() || "",
        telefono1: esReservaExterna
          ? (formData.telefono1?.trim() || "")
          : formData.telefono1!.trim(),
        telefono2: formData.telefono2?.trim() || "",
        notas: formData.notas?.trim() || "",
        precio_personalizado: esEstadiaInterna
          ? null
          : formData.precioPersonalizado || null,
        pago: esEstadiaInterna ? 0 : formData.sena || 0,
        cantidad_huespedes: formData.cantidad_huespedes || 1,
        tipo_cama: formData.tipo_cama || ["individual"],
        acompanantes: formData.acompanantes || [],
        afiliado_nombre: formData.afiliado_nombre?.trim() || "",
        afiliado_apellido: formData.afiliado_apellido?.trim() || "",
        afiliado_dni: formData.afiliado_dni?.trim() || "",
        late_checkout: esEstadiaInterna ? false : formData.late_checkout || false,
        sin_limpieza_automatica: formData.sin_limpieza_automatica || false,
        ajuste_financiero: esEstadiaInterna ? 0 : formData.ajuste_financiero || 0,
        tipo_ajuste: esEstadiaInterna ? "ninguno" : formData.tipo_ajuste || "ninguno",
        hora_inicio: formData.hora_inicio || "14:00",
        hora_fin: formData.hora_fin || "10:00",
        cliente_id: formData.cliente_id ?? selectedCliente?.id ?? 0,
        valor_total: esEstadiaInterna ? 0 : formData.valorTotal || 0,
        valor_original: esEstadiaInterna ? 0 : formData.valorTotal || 0,
        estado: "confirmada",
        es_empresa: esEmpresa,
        origen_reserva: origenReserva,
        codigo_reserva_externa: codigoReservaExterna,
        codigo_booking: esBooking ? codigoReservaExterna : null,
      };

      const newReservation = await createReservationMutation.mutateAsync(
        reservaData
      );

      setSelectedCliente(null);
      setEsEmpresa(false);
      setEsBooking(false);
      setEsAirbnb(false);
      setFormData({
        entrada: undefined,
        salida: undefined,
        departamento: "",
        nombre: "",
        apellido: "",
        dni: "",
        edad: "",
        localidad: "",
        notas: "",
        email1: "",
        email2: "",
        telefono1: "",
        telefono2: "",
        acompanantes: [],
        valorTotal: undefined,
        precioPersonalizado: undefined,
        sena: 0,
        cantidad_huespedes: 1,
        tipo_cama: ["individual"],
        afiliado_nombre: "",
        afiliado_apellido: "",
        afiliado_dni: "",
        late_checkout: false,
        sin_limpieza_automatica: false,
        ajuste_financiero: 0,
        tipo_ajuste: "ninguno",
        hora_inicio: "14:00",
        hora_fin: "10:00",
        cliente_id: undefined,
      });

      onSuccess(newReservation);
    } catch (error: unknown) {
      logger.errorWithContext("Error al crear la reserva", error);
      setErrorMsg(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error ||
          "Error al crear la reserva. Por favor, inténtalo de nuevo."
      );
    }
  };

  const createEstacionamientoReservation = async () => {
    try {
      const reservaData = {
        entrada: formatDateKey(formData.entrada!),
        salida: formatDateKey(formData.salida!),
        estacionamiento: formData.estacionamiento!,
        nombre: formData.nombre!.trim(),
        apellido: formData.apellido!.trim(),
        dni: formData.dni!.trim(),
        notas: formData.notas_estacionamiento?.trim() || "",
        pago: formData.pago_estacionamiento || 0,
        valor_total: formData.valor_total_estacionamiento || 0,
        cliente_id: formData.cliente_id ?? selectedCliente?.id ?? null,
        precio_diario_congelado:
          formData.precio_estacionamiento_por_dia || null,
        departamento_asociado: formData.departamento_asociado || null,
        cobrado_por: formData.cobrado_por_estacionamiento || null,
        hora_inicio: "14:00",
        hora_fin: "10:00",
      };

      const newReservation =
        await createEstacionamientoReservationMutation.mutateAsync(reservaData);

      onSuccess(newReservation);
    } catch (error: unknown) {
      logger.errorWithContext(
        "Error al crear la reserva de estacionamiento",
        error
      );
      setErrorMsg(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Error al crear la reserva de estacionamiento."
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    const estacionamientoFilled = !!String(
      formData.estacionamiento || ""
    ).trim();
    const departamentoFilled = !!String(formData.departamento || "").trim();
    const parkingSecondary = estacionamientoFilled && !departamentoFilled;
    const isParkingFlow = soloEstacionamiento || parkingSecondary;

    setIsSubmitting(true);
    try {
      if (isParkingFlow) {
        const validationErrors = await validateEstacionamientoForm(formData);
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return;
        }
        await createEstacionamientoReservation();
        return;
      }

      try {
        const validationErrors = await validateForm(
          formData,
          existingReservations,
          {
            esBooking,
            esAirbnb,
            esEmpresa,
            clienteIdSeleccionado: (() => {
              const id = formData.cliente_id ?? selectedCliente?.id;
              return id != null && id > 0 ? id : null;
            })(),
          }
        );

        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return;
        }

        await createReservation();
      } catch (error) {
        logger.error("Error en validación:", error);
        setErrorMsg("Error en la validación del formulario.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClienteSelect = (cliente: Cliente) => {
    docValidationSeqRef.current += 1;
    setSelectedCliente(cliente);
    selectedClienteRef.current = cliente;
    const booking = cliente.origen_reserva === "booking";
    const airbnb = cliente.origen_reserva === "airbnb";
    const emp = !!cliente.es_empresa;
    setEsEmpresa(emp);
    esEmpresaRef.current = emp;
    setEsBooking(booking);
    setEsAirbnb(airbnb && !booking);

    setFormData((prev) => ({
      ...prev,
      nombre: cliente.nombre,
      apellido: cliente.apellido,
      dni: cliente.dni || "",
      telefono1: cliente.telefono || "",
      email1: cliente.email || "",
      localidad: cliente.localidad || "",
      cliente_id: cliente.id,
    }));
    setErrors((prev) => ({ ...prev, dni: undefined }));
    setDocumentoWarning(undefined);
  };

  const handleClienteClear = () => {
    docValidationSeqRef.current += 1;
    setSelectedCliente(null);
    selectedClienteRef.current = null;
    setDocumentoWarning(undefined);
    setErrors((prev) => ({ ...prev, dni: undefined }));
    setEsEmpresa(false);
    esEmpresaRef.current = false;
    setEsBooking(false);
    setEsAirbnb(false);
    setFormData((prev) => ({
      ...prev,
      nombre: "",
      apellido: "",
      dni: "",
      telefono1: "",
      email1: "",
      localidad: "",
      cliente_id: undefined,
    }));
  };

  const cambiarTipo = useCambioTipoCliente();

  const handleEsEmpresaChange = (checked: boolean) => {
    if (checked === esEmpresaRef.current) return;
    // Persona ↔ empresa: los dos campos se unen en la razón social, o vuelve
    // lo escrito antes. Se calcula fuera de setFormData porque recuerda estado.
    const nombre = cambiarTipo(
      { nombre: formDataRef.current.nombre || "", apellido: formDataRef.current.apellido || "" },
      checked
    );
    setFormData((prev) => ({ ...prev, ...nombre }));
    setErrors((prev) => ({ ...prev, nombre: undefined, apellido: undefined }));
    setEsEmpresa(checked);
    esEmpresaRef.current = checked;
    if (esBooking || esAirbnb) return;
    const raw = formDataRef.current.dni || "";
    if (!onlyDigits(raw)) {
      setDocumentoWarning(undefined);
      setErrors((prev) => ({ ...prev, dni: undefined }));
      return;
    }
    runDirectoDocumentoValidation(raw, checked);
  };

  const handleEsBookingChange = (checked: boolean) => {
    setEsBooking(checked);
    if (checked) {
      docValidationSeqRef.current += 1;
      setDocumentoWarning(undefined);
      setErrors((prev) => ({ ...prev, dni: undefined }));
      setEsAirbnb(false);
    }
  };

  const handleEsAirbnbChange = (checked: boolean) => {
    setEsAirbnb(checked);
    if (checked) {
      docValidationSeqRef.current += 1;
      setDocumentoWarning(undefined);
      setErrors((prev) => ({ ...prev, dni: undefined }));
      setEsBooking(false);
    }
  };

  const savePending =
    isSubmitting ||
    createReservationMutation.isPending ||
    createEstacionamientoReservationMutation.isPending;

  const hasSelectedCliente = useMemo(
    () =>
      selectedCliente !== null ||
      (formData.cliente_id != null && Number(formData.cliente_id) > 0),
    [selectedCliente, formData.cliente_id]
  );

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    documentoWarning,
    isSubmitting,
    savePending,
    isInternalStay,
    setIsSubmitting,
    zonas,
    categoriaDepartamento,
    setCategoriaDepartamento,
    dateRange,
    setDateRange,
    dateRangeAnchorEl,
    setDateRangeAnchorEl,
    loadingZonas,
    errorMsg: errorZonas || errorMsg,
    setErrorMsg,
    activeTab,
    handleSubmit,
    handleTextChange,
    handleSelectChange,
    handleDateRangeClick,
    handleDateRangeClose,
    handleDateRangeChange,
    handleTabChange,
    overlapError,
    selectedCliente,
    hasSelectedCliente,
    handleClienteSelect,
    handleClienteClear,
    esEmpresa,
    handleEsEmpresaChange,
    esBooking,
    handleEsBookingChange,
    esAirbnb,
    handleEsAirbnbChange,
    esReservaExterna: esBooking || esAirbnb,
    continueWithReservation,
    handleInputChange,
  };
};
